import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import '../../v2-theme.css';

function CheckoutWorkerPro() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState(user?.email || '');
  const [name, setName] = useState('');

  const pricing = {
    monthly: { amount: 9.99, display: '$9.99/month', priceId: process.env.REACT_APP_STRIPE_WORKER_MONTHLY_PRICE_ID },
    annual: { amount: 99, display: '$99/year', priceId: process.env.REACT_APP_STRIPE_WORKER_ANNUAL_PRICE_ID }
  };

  const currentPrice = pricing[billingCycle];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Step 1: Create payment method
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          name,
          email,
        },
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Step 2: Send payment method to server to create subscription
      const { data } = await api.post('/subscriptions/create-subscription', {
        paymentMethodId: paymentMethod.id,
        email,
        name,
        plan: 'worker_professional',
        billingCycle,
        priceId: currentPrice.priceId
      });

      // Step 3: Handle subscription response
      if (data.clientSecret) {
        // Requires additional authentication (3D Secure)
        const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);

        if (confirmError) {
          throw new Error(confirmError.message);
        }
      }

      // Success! Redirect to success page
      navigate('/payment-success', {
        state: {
          type: 'worker_professional',
          amount: currentPrice.amount,
          billingCycle,
          message: 'Welcome to Professional! Your account has been upgraded.'
        }
      });
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.response?.data?.error || err.message || 'Subscription failed');
    } finally {
      setLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        color: '#FFFFFF',
        fontFamily: '"Work Sans", sans-serif',
        fontSize: '16px',
        '::placeholder': {
          color: '#C0C0C0',
        },
      },
      invalid: {
        color: '#fa755a',
      },
    },
  };

  return (
    <div className="v2-page-wrapper">
      <div style={{ maxWidth: '900px', width: '100%', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <div className="v2-header-center" style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '20px' }}>
            <img src="/hotf-web-icon.png" alt="Hands On The Fly" style={{ width: '50px', height: '50px' }} />
            <h1 style={{ fontFamily: 'Times New Roman, serif', fontSize: '1.8rem', fontWeight: 'normal' }}>
              Hands On The Fly
            </h1>
          </div>
        </div>

        <h2 className="v2-heading-xl" style={{ textAlign: 'center', marginBottom: '10px' }}>
          Upgrade to Professional
        </h2>
        <p className="v2-text-muted" style={{ textAlign: 'center', marginBottom: '40px' }}>
          Unlock all features and maximize your earning potential
        </p>

        {/* Billing Cycle Toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '40px' }}>
          <button
            onClick={() => setBillingCycle('monthly')}
            style={{
              padding: '15px 30px',
              background: billingCycle === 'monthly' ? 'var(--steel)' : 'transparent',
              border: `2px solid ${billingCycle === 'monthly' ? 'var(--steel)' : 'var(--dark-steel)'}`,
              color: billingCycle === 'monthly' ? 'var(--charcoal)' : 'var(--silver)',
              fontFamily: 'Oswald, sans-serif',
              fontSize: '1rem',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            Monthly - $9.99
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            style={{
              padding: '15px 30px',
              background: billingCycle === 'annual' ? 'var(--steel)' : 'transparent',
              border: `2px solid ${billingCycle === 'annual' ? 'var(--steel)' : 'var(--dark-steel)'}`,
              color: billingCycle === 'annual' ? 'var(--charcoal)' : 'var(--silver)',
              fontFamily: 'Oswald, sans-serif',
              fontSize: '1rem',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            Annual - $99 {' '}
            <span style={{
              display: 'inline-block',
              marginLeft: '8px',
              padding: '2px 8px',
              background: '#4CAF50',
              color: 'white',
              fontSize: '0.75rem',
              borderRadius: '3px',
              fontWeight: '600'
            }}>
              Save $20.88
            </span>
          </button>
        </div>

        {/* Checkout Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '30px' }}>
          {/* Plan Summary */}
          <div className="v2-card" style={{ padding: '30px', border: '2px solid var(--white)' }}>
            <div style={{
              background: 'var(--white)',
              color: 'var(--charcoal)',
              padding: '6px 14px',
              fontSize: '0.75rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              display: 'inline-block',
              marginBottom: '20px'
            }}>
              Worker Professional
            </div>

            <div className="v2-heading-lg" style={{ marginBottom: '15px' }}>
              Professional
            </div>

            <div style={{ fontSize: '2.5rem', fontWeight: '600', marginBottom: '20px' }}>
              {billingCycle === 'monthly' ? '$9.99' : '$99'}
              <span style={{ fontSize: '1rem', color: 'var(--silver)', fontWeight: '400' }}>
                /{billingCycle === 'monthly' ? 'month' : 'year'}
              </span>
            </div>

            <div style={{ textAlign: 'center', color: 'var(--silver)', fontSize: '0.9rem', marginBottom: '20px' }}>
              {billingCycle === 'monthly' ? 'Billed monthly • Cancel anytime' : 'Billed annually • Save $20.88/year • Cancel anytime'}
            </div>

            <ul style={{ listStyle: 'none', marginBottom: '20px' }}>
              {[
                'Multiple worker roles',
                'Profile photo upload',
                'Professional description',
                'CV/Resume upload',
                'ServSafe certification display',
                'Priority visibility to restaurants',
                'Advanced matching algorithm',
                'Enhanced profile badge'
              ].map((feature, index) => (
                <li key={index} style={{
                  padding: '10px 0',
                  borderBottom: '1px solid var(--dark-steel)',
                  fontSize: '0.9rem'
                }}>
                  <span style={{ color: 'var(--white)', marginRight: '10px' }}>✓</span>
                  {feature}
                </li>
              ))}
            </ul>

            <div style={{
              padding: '15px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderLeft: '3px solid var(--white)',
              marginTop: '20px'
            }}>
              <strong>Billing:</strong> ${billingCycle === 'monthly' ? '9.99 charged monthly' : '99 charged annually'}<br />
              {billingCycle === 'annual' && <><strong>Save $20.88</strong> compared to monthly • </>}
              <strong>Cancel anytime</strong> - No long-term commitment
            </div>
          </div>

          {/* Payment Form */}
          <div className="v2-card" style={{ padding: '30px' }}>
            <form onSubmit={handleSubmit}>
              {/* Account Information */}
              <div style={{ marginBottom: '30px' }}>
                <div className="v2-heading-md" style={{ marginBottom: '15px' }}>
                  Account Information
                </div>

                <div className="v2-form-group">
                  <label className="v2-label">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="john.doe@email.com"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--charcoal)',
                      border: '1px solid var(--dark-steel)',
                      color: 'var(--white)',
                      fontFamily: 'Work Sans, sans-serif',
                      fontSize: '1rem',
                      borderRadius: '2px'
                    }}
                  />
                </div>

                <div className="v2-form-group">
                  <label className="v2-label">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="John Doe"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--charcoal)',
                      border: '1px solid var(--dark-steel)',
                      color: 'var(--white)',
                      fontFamily: 'Work Sans, sans-serif',
                      fontSize: '1rem',
                      borderRadius: '2px'
                    }}
                  />
                </div>
              </div>

              {/* Payment Information */}
              <div style={{ marginBottom: '30px' }}>
                <div className="v2-heading-md" style={{ marginBottom: '15px' }}>
                  Payment Information
                </div>

                <div className="v2-form-group">
                  <label className="v2-label">Card Details</label>
                  <div style={{
                    padding: '12px',
                    background: 'var(--charcoal)',
                    border: '1px solid var(--dark-steel)',
                    borderRadius: '2px'
                  }}>
                    <CardElement options={cardElementOptions} />
                  </div>
                  {error && (
                    <div style={{ color: '#fa755a', fontSize: '0.85rem', marginTop: '8px' }}>
                      {error}
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                className="v2-btn v2-btn-primary"
                disabled={!stripe || loading}
                style={{ width: '100%', marginTop: '10px' }}
              >
                {loading ? 'Processing...' : `Subscribe Now - ${currentPrice.display}`}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '10px', color: 'var(--silver)', fontSize: '0.8rem' }}>
                <span>🔒 Secure SSL</span>
                <span>💳 PCI Compliant</span>
                <span>✓ Stripe Verified</span>
              </div>
              <p style={{ color: 'var(--dark-steel)', fontSize: '0.85rem' }}>
                Your payment information is encrypted and secure.
              </p>
            </div>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={() => navigate('/profile/worker')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--silver)',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  transition: 'color 0.3s ease'
                }}
              >
                ← Cancel and return to profile
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(26, 26, 26, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="v2-spinner"></div>
        </div>
      )}
    </div>
  );
}

export default CheckoutWorkerPro;
